import { Injectable } from "@angular/core";
import { Row } from "@components/main/spreading/models/models";
import { SpreadingState } from "@components/main/spreading/state/manager";

export type ScaleType =
  | "ACTUALS"
  | "HUNDREDS"
  | "THOUSANDS"
  | "MILLIONS"
  | "BILLIONS"
  | "TRILLIONS"
  | "LAKHS"
  | "CRORES";

export const ROUNDING_MAP: Record<ScaleType, string> = {
  ACTUALS: "Actuals",
  HUNDREDS: "Hundreds",
  THOUSANDS: "Thousands",
  MILLIONS: "Millions",
  BILLIONS: "Billions",
  TRILLIONS: "Trillions",
  LAKHS: "Lakhs",
  CRORES: "Crores",
};

export const SCALE_MULTIPLIERS: Record<ScaleType, number> = {
  ACTUALS: 1,
  HUNDREDS: 100,
  THOUSANDS: 1_000,
  MILLIONS: 1_000_000,
  BILLIONS: 1_000_000_000,
  TRILLIONS: 1_000_000_000_000,
  LAKHS: 100_000,
  CRORES: 10_000_000,
};

@Injectable({
  providedIn: "root",
})
export class RoundingService {
  /**
   * Apply rounding and multiplication
   */
  roundRows(state: SpreadingState) {
    if (!state) {
      return;
    }
    let scaleType = <ScaleType>state?.reviewQueueItem?.multiplyByFactor;
    if (!scaleType) {
      console.warn(
        "Multiply By Factor is empty, will not apply rounding/multiplication"
      );
      scaleType = "ACTUALS";
    }

    this.multiplyRows(state.sourceRows, scaleType);
    this.multiplyRows(state.rows, scaleType);
    this.multiplyRows(state.statement?.rows, scaleType);
  }

  /**
   * Get rounded single value number form a rounded value
   * This is usually called when user input a number in the cell and end editing
   * We want to unround it to actual to do calculation and data save
   */
  roundCellValue(state: SpreadingState, row: Row, cellValue: number) {
    let scaleType = <ScaleType>state?.reviewQueueItem?.multiplyByFactor;
    if (!scaleType) {
      console.warn(
        "Multiply By Factor is empty, will not apply rounding/multiplication"
      );
      scaleType = "ACTUALS";
    }
    let multiplier = SCALE_MULTIPLIERS[scaleType];
    if (this.excludeFromRoundingAndMultiplication(row)) {
      // Do not round if line item is excluded from rounding/multiplication
      multiplier = 1;
    }
    const roundedVal = cellValue ? cellValue / multiplier : cellValue;
    return roundedVal;
  }

  /**
   * Get unrounded single value number form a rounded value
   * This is usually called when user input a number in the cell
   * We want to unround it to actual to do calculation and data save
   */
  unRoundCellValue(state: SpreadingState, row: Row, cellValue: number) {
    let scaleType = <ScaleType>state?.reviewQueueItem?.multiplyByFactor;
    if (!scaleType) {
      console.warn(
        "Multiply By Factor is empty, will not apply rounding/multiplication"
      );
      scaleType = "ACTUALS";
    }
    let multiplier = SCALE_MULTIPLIERS[scaleType];
    if (this.excludeFromRoundingAndMultiplication(row)) {
      // Do not round if line item is excluded from rounding/multiplication
      multiplier = 1;
    }
    const actualVal = cellValue ? cellValue * multiplier : cellValue;
    return actualVal;
  }

  /**
   * Recursively unscale all cell values in rows.
   */
  private multiplyRows(
    rows: Row[],
    scaleType: ScaleType,
  ): void {
    if (!rows) {
      return;
    }
    let multiplier;
    for (const row of rows) {
      // Scale all cell values
      if (Array.isArray(row.cells)) {
        multiplier = SCALE_MULTIPLIERS[scaleType];
        if (this.excludeFromRoundingAndMultiplication(row)) {
          // Do not round if line item is excluded from rounding/multiplication
          multiplier = 1;
        }
        for (const cell of row.cells) {
          if (typeof cell.value === "number") {
            const rawTextParsed = cell.rawText ? parseFloat(cell.rawText) : undefined;
            cell.roundedRawText = rawTextParsed ? (rawTextParsed / multiplier).toString() : cell.rawText;
          }
        }
      }
      // Recurse into children
      if (row.children && row.children.length > 0) {
        this.multiplyRows(row.children, scaleType);
      }
    }
  }

  public excludeFromRoundingAndMultiplication(row: Row): boolean {
    return (
      row?.excludeFromRoundingAndMultiplication || ["PERCENT", "RATIO", "DAYS"].includes(row?.textFormat)
    );
  }
}
